import axios from "axios";

/**
 * Fetches data from MongoDB via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} _id - The document ID to retrieve
 * @returns {Promise} - Returns the data from the API
 */
const getDataFromMongoDb = async (collectionName, _id) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/get-dashboard-data";
        
        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                _id: _id
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data.data;
    } catch (error) {
        console.error("Error fetching data from MongoDB:", error);
        throw error;
    }
};

/**
 * Fetches data from a specific path within a MongoDB document via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} _id - The document ID to retrieve data from
 * @param {string} path - The nested path to retrieve (e.g., "data.2025.09.29")
 * @returns {Promise} - Returns the data from the specified path
 */
const getDataInMongoDbByPath = async (collectionName, _id, path) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/get-dashboard-data-by-path";

        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                _id: _id,
                path: path
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching data from MongoDB by path:", error);
        throw error;
    }
};

/**
 * Fetches data from a specific path within a MongoDB document via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} _id - The document ID to retrieve data from
 * @param {string} path - The nested path to retrieve (e.g., "data.2025.09.29")
 * @returns {Promise} - Returns the data from the specified path
 */
const getKeysInMongoDbByPath = async (collectionName, _id, path) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/get-graph-keys-by-path";

        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                _id: _id,
                path: path
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching data from MongoDB by path:", error);
        throw error;
    }
};

/**
 * Updates data in MongoDB via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {Array<string>} path - Array representing the nested path to update (e.g., ["airkauaihelicopters", "chatbot_data", "20"])
 * @param {string|Array|Object|any} value - The value to update (can be string, array, object, or any type)
 * @returns {Promise} - Returns the response from the API
 */
const updateDataInMongoDb = async (collectionName, graphName, path, value) => {
    try {
        const baseUrl = `https://server.causalfunnels.com/api/update-dashboard-data`;
        
        const response = await axios.put(baseUrl, {
            collectionName: collectionName,
            graphName: graphName,
            path: path,
            value: value
        }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error updating data in MongoDB:", error);
        if (error.response) {
            console.error("Response status:", error.response.status);
            console.error("Response data:", error.response.data);
        }
        throw error;
    }
};


 

/**
 * Fetches heatmap data keys via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {string} availableKeys - The available keys parameter
 * @param {string|number} year - The year parameter
 * @param {string|number} month - The month parameter
 * @returns {Promise} - Returns the response from the API
 */
const getHeatmapDataKeys = async (collectionName, graphName, availableKeys, year, month) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/heatmap-data-keys/available-keys";
        
        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                graphName: graphName,
                availableKeys: availableKeys,
                year: year,
                month: month
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error fetching heatmap data keys:", error);
        throw error;
    }
};

/**
 * Fetches heatmap data via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {string|number} year - The year parameter
 * @param {string|number} month - The month parameter
 * @param {string} week - The week parameter
 * @returns {Promise} - Returns the response from the API
 */
const getHeatmapData = async (collectionName, graphName, year, month, week) => {
    try {
        const baseUrl = "https://server.causalfunnels.com/api/heatmap-data";
        
        const response = await axios.get(baseUrl, {
            params: {
                collectionName: collectionName,
                graphName: graphName,
                year: year,
                month: month,
                week: week
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error fetching heatmap data:", error);
        throw error;
    }
};

/**
 * Deletes data from MongoDB via the proxy API
 * @param {string} collectionName - The name of the MongoDB collection
 * @param {string} graphName - The graph name identifier
 * @param {Array<string>} path - Array representing the nested path to delete (e.g., ["airkauaihelicopters", "chatbot_data", "20"])
 * @returns {Promise} - Returns the response from the API
 */
const deleteDataInMongoDb = async (collectionName, graphName, path) => {
    try {
        const baseUrl = `${process.env.REACT_SERVER_URL}/api/delete-dashboard-data`;
        
        const response = await axios({
            method: 'delete',
            url: baseUrl,
            data: {
                collectionName: collectionName,
                graphName: graphName,
                path: path
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error deleting data from MongoDB:", error);
        if (error.response) {
            console.error("Response status:", error.response.status);
            console.error("Response data:", error.response.data);
            
            // Check if it's a 404 with a specific message pattern (API might be working but returning wrong status)
            if (error.response.status === 404 && error.response.data && 
                error.response.data.message && 
                error.response.data.message.includes('Invalid array index')) {
                console.log("Note: This might be an API issue - deletion may have succeeded despite the 404 error");
            }
        }
        throw error;
    }
};

// ==================== A/B TEST LAUNCH FUNCTION ====================
/**
 * Launch A/B Test Function
 * @param {Object} testData - Complete test configuration
 * @param {string} website - Shop domain identifier
 * @returns {Promise<Object>} - Launch result with test details
 */
const launchABTest = async (testData, website) => {
  try {
    const baseUrl = `https://abtest.causalfunnels.com/api/${website}/${testData.metadata.testId}`;
    
    // Validate shop domain
    if (!website) {
      throw new Error("Shop domain is required");
    }
    
    // Validate test data (strict for launch)
    const validation = validateLaunchData(testData);
    if (!validation.valid) {
      const error = new Error("Validation failed");
      error.validationErrors = validation.errors;
      error.statusCode = 400;
      throw error;
    }
    
    // Construct the MongoDB document ID
    const documentId = testData.metadata.testId;
    
    // Determine test type for logging
    const testType = testData.basicInfo?.testType || testData.variants?.testType || (testData.elementConfig ? 'element' : 'unknown');
    
    console.log("🚀 Launching A/B Test:", {
      shop: website,
      testId: documentId,
      testName: testData.basicInfo?.testName,
      testType: testType
    });
    
    // Add launch timestamp and metadata
    testData.metadata.launchedAt = new Date().toISOString();
    testData._id = documentId;
    
    console.log("baseURl -> ",baseUrl, "testData->",testData);

    // Push data using axios
    const response = await axios.post(baseUrl, testData, {
      headers: {
        "Content-Type": "application/json"
      }
    });

    console.log("✅ Test launched successfully:", {
      testId: documentId,
      collection: website
    });
    
    // Return success result
    return {
      success: true,
      message: "Test launched successfully! 🎉",
      testId: documentId,
      collection: website,
      data: response.data
    };
    
  } catch (error) {
    console.error("❌ Error launching test:", error.message);
    
    throw {
      success: false,
      error: error.response?.data?.message || error.message || "Internal server error",
      details: error.validationErrors || [],
      statusCode: error.response?.status || 500
    };
  }
};

// Fetch the data of the tests for a given website 
const fetchAllTests = async (website) => {
    try {
        if (!website) {
            throw new Error("Shop domain is required");
        }

        // Construct API URL to fetch all tests for this shop
        const apiUrl = `https://abtest.causalfunnels.com/api/${website}`;
        console.log("📋 Fetching tests for shop:", website);

        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });
        // const response = await axios.get(apiUrl, {
        //     headers: {
        //         "Content-Type": "application/json",
        //     }
        // });

        console.log("Response received for fetching tests:", response);

        if (!response.ok) {
            console.error("❌ Failed to fetch tests");
            throw new Error(`Failed to fetch tests: ${response.status}`);
        }

        const result = await response.json();
        console.log("✅ Fetched tests successfully:", result.count);

        return {
            success: true,
            tests: result.data || [],
            count: result.count || 0
        };
    } catch (error) {
        console.error("❌ Error fetching tests:", error);
        return {
            success: false,
            error: error.message,
            tests: [],
            count: 0
        };
    }
};

/**
 * Update the status of an A/B test
 * @param {string} website - The shop domain
 * @param {string} testId - The test ID to update
 * @param {string} status - The new status (active, paused, stopped, completed, draft)
 * @returns {Promise<Object>} Result object with success status and data
 */
const updateTestStatus = async (website, testId, status) => {
    try {
        // Validate inputs
        if (!website) {
            throw new Error("Shop domain is required");
        }

        if (!testId) {
            throw new Error("Test ID is required");
        }

        if (!status) {
            throw new Error("Status is required");
        }

        // Validate status values
        const validStatuses = ['active', 'paused', 'stopped', 'completed', 'draft'];
        if (!validStatuses.includes(status)) {
            throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
        }

        // Construct the API URL for partial update (PATCH)
        const apiUrl = `https://abtest.causalfunnels.com/api/${website}/${testId}`;
        
        console.log("🔄 Updating test status:", {
            shop: website,
            testId,
            newStatus: status,
            apiUrl
        });

        // Prepare the update payload
        const updatePayload = {
            "metadata.status": status,
            "metadata.updatedAt": new Date().toISOString()
        };

        // Make PATCH request to your MongoDB server
        const response = await fetch(apiUrl, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(updatePayload)
        });

        const responseText = await response.text();
        let result;
        
        try {
            result = JSON.parse(responseText);
        } catch {
            result = { raw: responseText };
        }

        if (!response.ok) {
            console.error("❌ Failed to update test status:", result);
            throw new Error(result.error || "Failed to update test status");
        }

        console.log(`✅ Test status updated successfully: ${testId} -> ${status}`);

        return {
            success: true,
            message: `Test ${status} successfully`,
            testId,
            status,
            data: result
        };

    } catch (error) {
        console.error("❌ Error updating test status:", error);
        return {
            success: false,
            error: error.message || "Internal server error",
            testId,
            status
        };
    }
};

/**
 * Delete an A/B test
 * @param {string} website - The shop domain
 * @param {string} testId - The test ID to delete
 * @returns {Promise<Object>} Result object with success status and data
 */
const deleteTest = async (website, testId) => {
    try {
        // Validate inputs
        if (!website) {
            throw new Error("Shop domain is required");
        }

        if (!testId) {
            throw new Error("Test ID is required");
        }

        // Construct the API URL for deletion
        const apiUrl = `https://abtest.causalfunnels.com/api/${website}/${testId}`;
        
        console.log("🗑️ Deleting test:", {
            shop: website,
            testId,
            apiUrl
        });

        // Make DELETE request to your MongoDB server
        const response = await fetch(apiUrl, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            }
        });

        let result;
        
        // Try to parse JSON response, but handle non-JSON responses gracefully
        try {
            const responseText = await response.text();
            if (responseText.trim()) {
                result = JSON.parse(responseText);
            } else {
                result = { message: "Deleted successfully" };
            }
        } catch (parseError) {
            // If JSON parsing fails, check if the HTTP status indicates success
            if (response.ok) {
                result = { message: "Deleted successfully" };
            } else {
                console.error("❌ Failed to parse delete response:", parseError);
                throw new Error("Failed to delete test - Invalid response format");
            }
        }

        if (!response.ok) {
            console.error("❌ Failed to delete test:", result);
            throw new Error(result.error || result.message || "Failed to delete test");
        }

        console.log(`✅ Test deleted successfully: ${testId}`);

        return {
            success: true,
            message: "Test deleted successfully",
            testId,
            data: result
        };

    } catch (error) {
        console.error("❌ Error deleting test:", error);
        return {
            success: false,
            error: error.message || "Internal server error",
            testId
        };
    }
};

/**
 * Fetch metrics with optional filters
 * @param {Object} params - Query parameters
 * @param {string} params.website - Shop domain (required)
 * @param {string} params.testId - Test ID (optional, if provided fetches metrics for specific test)
 * @param {string} params.event - Event type filter (optional)
 * @param {string} params.group - Group filter (control/variant) (optional)
 * @param {string} params.startDate - Start date filter (optional)
 * @param {string} params.endDate - End date filter (optional)
 * @param {number} params.limit - Limit number of results (default: 1000)
 * @returns {Promise<Object>} Result object with success status and metrics data
 */
const fetchMetrics = async ({
    website,
    testId = null,
    event = null,
    group = null,
    startDate = null,
    endDate = null,
    limit = 1000
}) => {
    try {
        if (!website) {
            throw new Error("Shop domain is required");
        }

        // Build query params for API call
        const queryParams = new URLSearchParams();
        if (event) queryParams.set('event', event);
        if (group) queryParams.set('group', group);
        if (startDate) queryParams.set('startDate', startDate);
        if (endDate) queryParams.set('endDate', endDate);
        queryParams.set('limit', limit.toString());

        let apiUrl;

        if (testId) {
            // Get metrics for specific test
            apiUrl = `https://abtest.causalfunnels.com/api/${website}/metrics/${testId}`;
            if (queryParams.toString()) {
                apiUrl += `?${queryParams.toString()}`;
            }

            console.log(`📊 Fetching metrics for test ${testId}:`, apiUrl);

            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                }
            });

            if (!response.ok) {
                console.error(`❌ Failed to fetch metrics for test ${testId}`);
                throw new Error(`Failed to fetch test metrics: ${response.status}`);
            }

            const result = await response.json();
            console.log(`✅ Fetched ${result.count || 0} metrics for test ${testId}`);

            return {
                success: true,
                testId,
                metrics: result.data || [],
                count: result.count || 0
            };

        } else {
            // Get metrics for all tests by fetching from metrics collection
            apiUrl = `https://abtest.causalfunnels.com/api/${website}_metrics`;
            if (queryParams.toString()) {
                apiUrl += `?${queryParams.toString()}`;
            }

            console.log(`📊 Fetching all metrics for shop ${website}:`, apiUrl);

            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                }
            });

            if (!response.ok) {
                console.error(`❌ Failed to fetch metrics for shop ${website}`);
                throw new Error(`Failed to fetch metrics: ${response.status}`);
            }

            const result = await response.json();
            console.log(`✅ Fetched ${result.count || 0} total metrics for shop ${website}`);

            return {
                success: true,
                metrics: result.data || [],
                count: result.count || 0
            };
        }

    } catch (error) {
        console.error("❌ Error fetching metrics:", error);
        return {
            success: false,
            error: error.message,
            metrics: [],
            count: 0
        };
    }
};

/**
 * Fetch metrics summary for a specific test (aggregated data)
 * @param {string} website - Shop domain (required)
 * @param {string} testId - Test ID (required)
 * @returns {Promise<Object>} Result object with success status and summary data
 */
const fetchMetricsSummary = async (website, testId) => {
    try {
        if (!website) {
            throw new Error("Shop domain is required");
        }

        if (!testId) {
            throw new Error("Test ID is required");
        }

        // Get metrics summary for specific test
        const apiUrl = `https://abtest.causalfunnels.com/api/${website}/metrics/${testId}/summary`;

        console.log(`📊 Fetching metrics summary for test ${testId}`);

        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });

        if (!response.ok) {
            console.error(`❌ Failed to fetch metrics summary for test ${testId}`);
            throw new Error(`Failed to fetch metrics summary: ${response.status}`);
        }

        const result = await response.json();
        console.log(`✅ Fetched metrics summary for test ${testId}`);

        return {
            success: true,
            testId,
            summary: result.summary || []
        };

    } catch (error) {
        console.error("❌ Error fetching metrics summary:", error);
        return {
            success: false,
            error: error.message,
            summary: []
        };
    }
};

/**
 * Convenience function to fetch all metrics for a shop
 * @param {string} website - Shop domain
 * @returns {Promise<Object>} Result object with metrics data
 */
const fetchAllMetrics = async (website) => {
    return fetchMetrics({ website });
};

/**
 * Convenience function to fetch metrics for a specific test
 * @param {string} website - Shop domain
 * @param {string} testId - Test ID
 * @param {Object} filters - Optional filters (event, group, startDate, endDate, limit)
 * @returns {Promise<Object>} Result object with metrics data
 */
const fetchTestMetrics = async (website, testId, filters = {}) => {
    return fetchMetrics({ website, testId, ...filters });
};

// ==================== VALIDATION HELPER (Keep as is) ====================
function validateLaunchData(data) {
  const errors = [];

  // Validate metadata
  if (!data.metadata?.testId) {
    errors.push("Test ID is required");
  }
  if (!data.metadata?.status) {
    errors.push("Test status is required");
  }
  if (data.metadata?.status !== "active") {
    errors.push("Status must be 'active' for launch endpoint");
  }

  // Validate basic info
  if (!data.basicInfo?.testName || !data.basicInfo.testName.trim()) {
    errors.push("Test name is required");
  }

  // Validate settings
  if (!data.settings?.basic) {
    errors.push("Basic settings are required");
  } else {
    const { trafficSplit, duration, sampleSize, confidence } = data.settings.basic;

    if (!trafficSplit || trafficSplit < 10 || trafficSplit > 90) {
      errors.push("Traffic split must be between 10 and 90");
    }
    if (!duration || duration < 1) {
      errors.push("Duration must be at least 1 day");
    }
    if (!sampleSize || sampleSize < 100) {
      errors.push("Sample size must be at least 100");
    }
    if (!confidence || ![90, 95, 99].includes(confidence)) {
      errors.push("Confidence must be 90, 95, or 99");
    }
  }

  // Determine test type and validate accordingly
  const testType = data.basicInfo?.testType || 
                   data.variants?.testType || 
                   (data.elementConfig ? 'element' : null);

  if (!testType) {
    errors.push("Test type is required or could not be determined");
  }

  if (testType === 'element') {
    const elementData = data.elementConfig || data.variants;

    if (!elementData) {
      errors.push("Element configuration is required for element tests");
    } else {
      // Validate selectedButton
      if (!elementData.selectedButton) {
        errors.push("Selected button configuration is required");
      } else {
        const button = elementData.selectedButton;
        if (!button.name || !button.name.trim()) {
          errors.push("Button name is required");
        }
        if (!button.selector || !button.selector.trim()) {
          errors.push("Button CSS selector is required");
        }
        if (!button.pageUrl || !button.pageUrl.trim()) {
          errors.push("Button page URL is required");
        }

        // URL validation
        try {
          new URL(button.pageUrl);
        } catch {
          errors.push("Button page URL must be a valid URL");
        }
      }

      // Validate modifications
      if (!elementData.modifications) {
        errors.push("Element modifications configuration is required");
      } else {
        const modifications = elementData.modifications;

        if (!modifications.selector || !modifications.selector.trim()) {
          errors.push("Element selector is required in modifications");
        }
        if (!modifications.buttonName || !modifications.buttonName.trim()) {
          errors.push("Element button name is required in modifications");
        }

        // Validate position configuration if it exists
        if (modifications.position && modifications.position.test) {
          const position = modifications.position.test;

          if (!position.targetSelector || !position.targetSelector.trim()) {
            errors.push("Target selector is required for position changes");
          }
          if (!position.position || !position.position.trim()) {
            errors.push("Position method is required for position changes");
          }

          const validPositions = ['append', 'prepend', 'before', 'after'];
          if (!validPositions.includes(position.position)) {
            errors.push("Position method must be one of: append, prepend, before, after");
          }
        }
      }
    }
  } else if (testType === 'pricing') {
    if (!data.variants?.productId) {
      errors.push("Product ID is required for pricing tests");
    }
    if (!data.variants?.productTitle) {
      errors.push("Product title is required for pricing tests");
    }
    if (!data.variants?.variantPrices || Object.keys(data.variants.variantPrices).length === 0) {
      errors.push("At least one variant price is required for pricing tests");
    }

    if (data.variants?.variantPrices) {
      for (const [variantId, prices] of Object.entries(data.variants.variantPrices)) {
        if (!prices.originalPrice || !prices.testPrice) {
          errors.push(`Invalid prices for variant ${variantId}`);
        }
        if (prices.testPrice > prices.originalPrice) {
          errors.push(`Test price cannot be higher than original price for variant ${variantId}`);
        }
      }
    }
  } else if (testType === 'cart') {
    if (!data.variants?.triggerType) {
      errors.push("Cart discount trigger type is required");
    }
    if (!data.variants?.threshold || data.variants.threshold <= 0) {
      errors.push("Cart discount threshold must be greater than 0");
    }
    if (!data.variants?.discountType) {
      errors.push("Cart discount type is required");
    }
    if (!data.variants?.discountValue || data.variants.discountValue <= 0) {
      errors.push("Cart discount value must be greater than 0");
    }
    if (data.variants?.discountType === 'percentage' && data.variants.discountValue > 100) {
      errors.push("Percentage discount cannot exceed 100%");
    }
    if (!data.variants?.message || !data.variants.message.trim()) {
      errors.push("Cart discount message is required");
    }
    if (!data.variants?.displayPosition) {
      errors.push("Cart discount display position is required");
    }
  } else {
    errors.push(`Unsupported test type: ${testType}`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

export {
    getDataFromMongoDb,
    updateDataInMongoDb,
    deleteDataInMongoDb,
    getDataInMongoDbByPath,
    getKeysInMongoDbByPath,
    getHeatmapDataKeys,
    getHeatmapData,
    launchABTest,
    fetchAllTests,
    updateTestStatus,
    deleteTest,
    fetchMetrics,
    fetchMetricsSummary,
    fetchAllMetrics,
    fetchTestMetrics
};


export default { 
    getDataFromMongoDb, 
    updateDataInMongoDb, 
    deleteDataInMongoDb, 
    getDataInMongoDbByPath, 
    getKeysInMongoDbByPath,
    getHeatmapDataKeys,
    getHeatmapData,
    launchABTest,
    fetchAllTests,
    updateTestStatus,
    deleteTest,
    fetchMetrics,
    fetchMetricsSummary,
    fetchAllMetrics,
    fetchTestMetrics,
};